import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApiV2 } from '@/api/endpoints/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PrivacyPolicyContent } from '@/features/auth/components/PrivacyPolicyContent';
import { TermsOfServiceContent } from '@/features/auth/components/TermsOfServiceContent';
import { performSignOut } from '@/features/auth/lib/sign-out';
import { useAuthStore } from '@/stores/auth.store';

type LegalAcceptanceDialogProps = {
  /**
   * Which documents this user still owes. A one-sided version bump only
   * re-prompts the document that changed; the already-accepted one stays
   * readable but its checkbox is not required.
   */
  needsTerms: boolean;
  needsPrivacy: boolean;
};

/**
 * Blocking Terms of Service + Privacy Notice gate.
 *
 * Ports the legacy first-time-setup terms modal (`login.html`:
 * `data-backdrop="static"`, `data-keyboard="false"`, Accept or Close) to V3 and
 * adds Privacy as a second, independently versioned document. Escape and
 * backdrop clicks do nothing — the only exits are Accept (records both
 * acceptances) and Decline (signs out, keeps the account).
 */
export function LegalAcceptanceDialog({
  needsTerms,
  needsPrivacy,
}: LegalAcceptanceDialogProps) {
  const queryClient = useQueryClient();
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccept =
    (!needsTerms || termsChecked) && (!needsPrivacy || privacyChecked);

  async function handleAccept() {
    setIsSubmitting(true);
    setError(null);
    try {
      // The server re-reads the current versions, so send an affirmative for
      // both documents regardless of which checkbox the dialog required.
      await authApiV2.acceptLegal({ termsAccepted: true, privacyAccepted: true });
      // Surgical patch rather than setUser — setUser nulls pendingTwoFactor.
      useAuthStore.setState((state) => ({
        user: state.user
          ? {
              ...state.user,
              mustAcceptLegal: false,
              mustAcceptTerms: false,
              mustAcceptPrivacy: false,
            }
          : state.user,
      }));
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    } catch {
      setError('We could not record your acceptance. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDecline() {
    await performSignOut();
    window.location.assign('/login');
  }

  return (
    <Dialog open>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto bg-card text-foreground"
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        aria-describedby="legal-acceptance-description"
      >
        <DialogHeader>
          <DialogTitle className="text-foreground">
            Review and accept to continue
          </DialogTitle>
          <DialogDescription
            id="legal-acceptance-description"
            className="text-muted-foreground"
          >
            Before you use TruSpace, please review the Terms of Service and the
            Privacy Notice.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={needsTerms ? 'terms' : 'privacy'}>
          <TabsList>
            <TabsTrigger value="terms">Terms of Service</TabsTrigger>
            <TabsTrigger value="privacy">Privacy Policy</TabsTrigger>
          </TabsList>
          <TabsContent value="terms">
            <div className="max-h-[45vh] overflow-y-auto pr-2">
              <TermsOfServiceContent />
            </div>
          </TabsContent>
          <TabsContent value="privacy">
            <div className="max-h-[45vh] overflow-y-auto pr-2">
              <PrivacyPolicyContent />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-col gap-2 border-t border-border pt-4">
          {needsTerms && (
            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(event) => setTermsChecked(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input"
              />
              <span>I accept the Terms of Service</span>
            </label>
          )}
          {needsPrivacy && (
            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={privacyChecked}
                onChange={(event) => setPrivacyChecked(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input"
              />
              <span>I accept the Privacy Policy</span>
            </label>
          )}
        </div>

        {error && <p className="text-sm text-destructive-text">{error}</p>}

        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={handleDecline}
            disabled={isSubmitting}
            className="rounded-md border border-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            Decline and sign out
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!canAccept || isSubmitting}
            className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving…' : 'Accept and continue'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
